import { Circle, CircleUser, CreateCircleData, UpdateCircleData } from "@/types/circle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "./api-client";
import { PaginatedResponse } from "@/types/api-utils";
import { useTranslation } from "react-i18next";

export function listCircles(params?: { is_owned_by_efb_directly?: boolean; is_joined?: boolean }) {
  return api.get<PaginatedResponse<Circle>>("/circles", { params });
}

export function viewCircle(id: string) {
  return api.get<Circle>(`/circles/${id}`);
}

export function createCircle(data: CreateCircleData) {
  const formData = new FormData();
  formData.append("title", data.title);
  formData.append("description", data.description);
  if (data.cover_photo) {
    formData.append("cover_photo", data.cover_photo);
  }
  return api.post("/circles/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export function updateCircle(id: string, data: UpdateCircleData) {
  return api.patch(`/circles/${id}/`, data);
}

export function setDefaultCircle(id: string) {
  return api.post(`/circles/${id}/set_default/`);
}

export function joinCircle(data: { id: string; invited_by_user_id?: string }) {
  const requestData: any = {};
  if (data.invited_by_user_id) {
    requestData.invited_by_user_id = data.invited_by_user_id;
  }
  return api.post(`/circles/${data.id}/join/`, requestData);
}

export function useListCirclesQuery(params?: {
  is_owned_by_efb_directly?: boolean;
  is_joined?: boolean;
}) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["circles", params],
    queryFn: () => listCircles(params),
    select: (response) => {
      if (response.data.results) {
        response.data.results = response.data.results.map((circle) => {
          if (circle.is_owned_by_efb_directly) {
            return {
              ...circle,
              title: t("circle.efbCircleTitle"),
              description: t("circle.efbCircleDescription"),
            };
          }
          return circle;
        });
      }
      return response;
    },
  });
}

export function useViewCircleQuery(id: string, options?: { enabled: boolean }) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["circles", id],
    queryFn: () => viewCircle(id),
    select: (response) => {
      if (response.data.is_owned_by_efb_directly) {
        response.data.title = t("circle.efbCircleTitle");
        response.data.description = t("circle.efbCircleDescription");
      }
      return response;
    },
    ...options,
  });
}

export function useJoinCircleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: joinCircle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });
}

export function useSetDefaultCircleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setDefaultCircle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });
}

export function useEditCircleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCircleData }) => updateCircle(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });
}

export function useCreateCircleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCircle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });
}

export function listCircleMembers(
  circleId: string,
  params?: { page?: number; page_size?: number },
) {
  return api.get<PaginatedResponse<CircleUser>>(`/circles/${circleId}/members`, { params });
}

export function useListCircleMembersQuery(
  circleId: string,
  params?: { page?: number; page_size?: number },
) {
  return useQuery({
    queryKey: ["circle-members", circleId, params],
    queryFn: () => listCircleMembers(circleId, params),
    enabled: !!circleId,
  });
}
